// Arquivo: página de administração financeira (scaffold)
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useNavigate } from 'react-router-dom'
import { useFinancasStore } from '@/stores/financas.store'
import { usePermission } from '@/hooks/use-auth'
import { useState } from 'react'
import {
  Building2,
  Wrench,
  Users,
  TrendingUp,
  FileText,
  Banknote,
  Receipt,
  Heart,
  Target,
  Search
} from 'lucide-react'

export function FinancasAdministracao() {
  const navigate = useNavigate()
  useFinancasStore()
  usePermission(['admin'])
  const [searchTerm, setSearchTerm] = useState('')

  const tiposTransacao = [
    {
      id: 'entradas',
      titulo: 'Entradas',
      descricao: 'Dízimos, ofertas, missões e doações',
      icon: Banknote,
      cor: 'bg-emerald-600 dark:bg-emerald-500',
      rota: '/financas/administracao/entradas'
    },
    {
      id: 'despesas-operacionais',
      titulo: 'Despesas Operacionais',
      descricao: 'Contas de luz, água, telefone e outros custos fixos',
      icon: Receipt,
      cor: 'bg-rose-600 dark:bg-rose-500',
      rota: '/financas/administracao/despesas-operacionais'
    },
    {
      id: 'manutencao-reformas',
      titulo: 'Manutenção e Reforma',
      descricao: 'Reparos e melhorias na infraestrutura',
      icon: Wrench,
      cor: 'bg-amber-600 dark:bg-amber-500',
      rota: '/financas/administracao/manutencao-reforma'
    },
    {
      id: 'salarios',
      titulo: 'Salários',
      descricao: 'Pagamentos de funcionários com repetição mensal',
      icon: Users,
      cor: 'bg-blue-600 dark:bg-blue-500',
      rota: '/financas/administracao/salarios'
    },
    {
      id: 'investimentos',
      titulo: 'Investimentos',
      descricao: 'Aplicações financeiras e reservas de emergência',
      icon: TrendingUp,
      cor: 'bg-violet-600 dark:bg-violet-500',
      rota: '/financas/administracao/investimentos'
    },
    {
      id: 'impostos',
      titulo: 'Impostos',
      descricao: 'Taxas governamentais e obrigações fiscais',
      icon: FileText,
      cor: 'bg-slate-600 dark:bg-slate-500',
      rota: '/financas/administracao/impostos'
    },
    {
      id: 'missoes',
      titulo: 'Missões',
      descricao: 'Apoio a missionários e projetos evangelísticos',
      icon: Target,
      cor: 'bg-purple-600 dark:bg-purple-500',
      rota: '/financas/administracao/missoes'
    },
    {
      id: 'solicitacoes-ministeriais',
      titulo: 'Solicitações Ministeriais',
      descricao: 'Pedidos de verba dos ministérios da igreja',
      icon: Building2,
      cor: 'bg-teal-600 dark:bg-teal-500',
      rota: '/financas/administracao/solicitacoes-ministeriais'
    },
    {
      id: 'projetos-sociais',
      titulo: 'Projetos Sociais',
      descricao: 'Ações sociais e ajuda humanitária',
      icon: Heart,
      cor: 'bg-pink-600 dark:bg-pink-500',
      rota: '/financas/administracao/projetos-sociais'
    }
  ]

  // Filtrar tipos de transação baseado no termo de busca
  const tiposFiltrados = tiposTransacao.filter((tipo) =>
    tipo.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tipo.descricao.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Administração Financeira</h2>
          <p className="text-muted-foreground">
            Gerencie todos os aspectos financeiros da igreja
          </p>
        </div>
      </div>

      {/* Barra de pesquisa */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar tipos de transação..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Grid de cards dos tipos de transação */}
      <div 
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        role="grid"
        aria-label="Tipos de transações financeiras"
      >
        {tiposFiltrados.map((tipo) => {
          const IconComponent = tipo.icon
          return (
            <Card 
              key={tipo.id} 
              className="group hover:shadow-lg transition-all duration-200 flex flex-col focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
              role="gridcell"
              tabIndex={0}
              aria-labelledby={`card-title-${tipo.id}`}
              aria-describedby={`card-desc-${tipo.id}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  navigate(tipo.rota)
                }
              }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div 
                      className={`p-2 rounded-lg ${tipo.cor}`}
                      aria-hidden="true"
                    >
                      <IconComponent className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>
                <CardTitle 
                  id={`card-title-${tipo.id}`}
                  className="text-xl"
                >
                  {tipo.titulo}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between">
                <p 
                  id={`card-desc-${tipo.id}`}
                  className="text-sm text-muted-foreground line-clamp-3"
                >
                  {tipo.descricao}
                </p>

                <div className="mt-4">
                  <Button 
                    onClick={() => navigate(tipo.rota)}
                    className="w-full"
                    variant="default"
                    aria-label={`Gerenciar ${tipo.titulo.toLowerCase()}`}
                  >
                    Gerenciar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Mensagem quando nenhum resultado é encontrado */}
      {tiposFiltrados.length === 0 && (
        <div className="text-center py-12">
          <Search className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-semibold text-foreground">Nenhum tipo de transação encontrado</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Tente ajustar os termos de busca.
          </p>
        </div>
      )}
    </div>
  )
}
